/**
 * MyNotificationsPage - Kullanıcıya gönderilmiş (saklanan) push bildirimlerinin listesi.
 * Tüm roller erişebilir. Sayfalama, "Okunmamışlar" filtresi ve tümünü okundu işaretleme.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, List, Segmented, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/tr';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';
import { all_routes } from '../../../router/all_routes';
import userNotificationService from '../../../services/notification/userNotification.service';
import { UserNotificationItem } from '../../../types/userNotification.types';
import { handleApiError } from '../../../utils/errorHandler';
import { getNotificationTargetRoute } from '../../../utils/notificationRoutes';
import {
  useUserNotificationUnreadCount,
  userNotificationUnreadStore,
} from '../../../hooks/useUserNotificationUnreadCount';

dayjs.extend(relativeTime);

type FilterKey = 'all' | 'unread';

const MyNotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routes = all_routes;
  const user = useSelector((state: RootState) => state.auth.user);
  const unreadCount = useUserNotificationUnreadCount();

  const [items, setItems] = useState<UserNotificationItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [loading, setLoading] = useState<boolean>(false);
  const [markingAll, setMarkingAll] = useState<boolean>(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userNotificationService.getNotifications({
        page,
        limit: pageSize,
        unreadOnly: filter === 'unread',
      });
      setItems(response?.items ?? []);
      setTotal(response?.total ?? 0);
      if (typeof response?.unreadCount === 'number') {
        userNotificationUnreadStore.set(response.unreadCount);
      }
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter, t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notification: UserNotificationItem) => {
    if (notification.isRead) return;
    setItems((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
    );
    try {
      const response = await userNotificationService.markAsRead(notification.id);
      userNotificationUnreadStore.set(response?.unreadCount ?? 0);
    } catch (error: any) {
      handleApiError(error, t);
      fetchNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await userNotificationService.markAllAsRead();
      userNotificationUnreadStore.set(0);
      message.success('Tüm bildirimler okundu olarak işaretlendi');
      if (filter === 'unread') {
        setPage(1);
        fetchNotifications();
      } else {
        setItems((prev) => prev.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt: new Date().toISOString() })));
      }
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleItemClick = (notification: UserNotificationItem) => {
    markAsRead(notification);
    const target = getNotificationTargetRoute(notification.data, user?.permissions);
    if (target) navigate(target);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Bildirimlerim</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Bildirimlerim</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <Button icon={<i className="ti ti-reload me-2" />} onClick={fetchNotifications} loading={loading}>
              Yenile
            </Button>
            <Button
              type="primary"
              icon={<i className="ti ti-checks me-2" />}
              onClick={handleMarkAllRead}
              loading={markingAll}
              disabled={unreadCount === 0}
            >
              Tümünü okundu işaretle
            </Button>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <Segmented
              options={[
                { label: 'Tümü', value: 'all' },
                { label: `Okunmamışlar (${unreadCount})`, value: 'unread' },
              ]}
              value={filter}
              onChange={(value) => {
                setFilter(value as FilterKey);
                setPage(1);
              }}
            />
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              {total} bildirim
            </span>
          </div>
          <div className="card-body p-0">
            <List<UserNotificationItem>
              loading={loading}
              dataSource={items}
              locale={{
                emptyText: filter === 'unread' ? 'Okunmamış bildiriminiz yok' : 'Henüz bildiriminiz yok',
              }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50],
                hideOnSinglePage: total <= 10,
                onChange: (nextPage, nextPageSize) => {
                  if (nextPageSize !== pageSize) {
                    setPageSize(nextPageSize);
                    setPage(1);
                  } else {
                    setPage(nextPage);
                  }
                },
                style: { padding: '0 16px 16px' },
              }}
              renderItem={(notification) => {
                const hasTarget = !!getNotificationTargetRoute(notification.data, user?.permissions);
                return (
                  <List.Item
                    key={notification.id}
                    onClick={() => handleItemClick(notification)}
                    style={{
                      padding: '14px 16px',
                      cursor: hasTarget || !notification.isRead ? 'pointer' : 'default',
                      backgroundColor: notification.isRead ? undefined : 'rgba(24, 144, 255, 0.06)',
                      borderLeft: notification.isRead ? '3px solid transparent' : '3px solid #1890ff',
                    }}
                    actions={
                      notification.isRead
                        ? hasTarget
                          ? [<i key="go" className="ti ti-chevron-right text-muted" />]
                          : []
                        : [
                            <Button
                              key="read"
                              type="link"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification);
                              }}
                            >
                              Okundu işaretle
                            </Button>,
                          ]
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        <span
                          className="d-inline-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: 40,
                            height: 40,
                            backgroundColor: notification.isRead ? '#f5f5f5' : 'rgba(24, 144, 255, 0.15)',
                          }}
                        >
                          <i
                            className={`ti ti-bell fs-5 ${notification.isRead ? 'text-muted' : 'text-primary'}`}
                          />
                        </span>
                      }
                      title={
                        <span className={notification.isRead ? 'fw-normal' : 'fw-semibold'}>
                          {notification.title}
                        </span>
                      }
                      description={
                        <div>
                          <div style={{ whiteSpace: 'pre-line', color: '#595959' }}>{notification.content}</div>
                          <Tooltip title={dayjs(notification.createdAt).format('DD.MM.YYYY HH:mm')}>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {dayjs(notification.createdAt).format('DD.MM.YYYY HH:mm')} ·{' '}
                              {dayjs(notification.createdAt).locale('tr').fromNow()}
                            </span>
                          </Tooltip>
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyNotificationsPage;
